import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ images = [], onChange, maxImages = 5 }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setError(null);
        const remainingSlots = maxImages - images.length;
        if (remainingSlots <= 0) {
            toast.error(`Maximum ${maxImages} images allowed`);
            return;
        }

        const filesToUpload = files.slice(0, remainingSlots);
        setUploading(true);

        try {
            const token = localStorage.getItem('hmc_token');
            if (!token) {
                toast.error('Please log in to upload images');
                setUploading(false);
                return;
            }

            const uploadedUrls = [];
            
            // Upload files one by one for better error handling
            for (const file of filesToUpload) {
                // Validate file type
                if (!file.type.startsWith('image/')) {
                    toast.error(`${file.name} is not an image`);
                    continue;
                }
                
                // Validate file size (5MB)
                if (file.size > 5 * 1024 * 1024) {
                    toast.error(`${file.name} is too large. Max 5MB`);
                    continue;
                }

                const formData = new FormData();
                formData.append('file', file);

                try {
                    const response = await axios.post(
                        `${BACKEND_URL}/api/upload`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': `Bearer ${token}`
                            }
                        }
                    );

                    const url = response.data.url;
                    const fullUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
                    uploadedUrls.push(fullUrl);
                } catch (err) {
                    console.error(`Failed to upload ${file.name}:`, err);
                    toast.error(`Failed to upload ${file.name}`);
                }
            }

            if (uploadedUrls.length > 0) {
                onChange([...images, ...uploadedUrls]);
                toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
            }
        } catch (error) {
            console.error('Upload error:', error);
            setError('Failed to upload images. Please try again.');
            toast.error('Failed to upload images');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const removeImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        onChange(newImages);
        toast.success('Image removed');
    };

    const triggerFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                    Listing Images ({images.length}/{maxImages})
                </label>
                {images.length < maxImages && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={triggerFileSelect}
                        disabled={uploading}
                        data-testid="upload-images-btn"
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4 mr-2" />
                                Add Images
                            </>
                        )}
                    </Button>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
            />

            {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                </div>
            )}

            {images.length === 0 ? (
                <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50/50 transition-all"
                    onClick={triggerFileSelect}
                    data-testid="upload-dropzone"
                >
                    {uploading ? (
                        <>
                            <Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-3 animate-spin" />
                            <p className="text-gray-600 mb-1 font-medium">Uploading...</p>
                        </>
                    ) : (
                        <>
                            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-600 mb-1 font-medium">Click to upload images</p>
                            <p className="text-sm text-gray-400">PNG, JPG, WebP up to 5MB each</p>
                        </>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {images.map((url, index) => (
                        <div 
                            key={index} 
                            className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group border border-gray-200"
                            data-testid={`image-preview-${index}`}
                        >
                            <img
                                src={url}
                                alt={`Listing image ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/200?text=Error';
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                data-testid={`remove-image-${index}`}
                            >
                                <X className="w-4 h-4" />
                            </button>
                            {index === 0 && (
                                <span className="absolute bottom-1 left-1 bg-amber-500 text-white text-xs px-2 py-0.5 rounded font-medium">
                                    Main
                                </span>
                            )}
                        </div>
                    ))}
                    {images.length < maxImages && !uploading && (
                        <div 
                            className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-amber-500 hover:bg-amber-50/50 transition-all"
                            onClick={triggerFileSelect}
                        >
                            <Upload className="w-6 h-6 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-400">Add more</span>
                        </div>
                    )}
                    {uploading && (
                        <div className="aspect-square rounded-lg border-2 border-dashed border-amber-300 flex items-center justify-center bg-amber-50">
                            <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                        </div>
                    )}
                </div>
            )}

            <p className="text-xs text-gray-500">
                First image will be the main listing photo. Max {maxImages} images.
            </p>
        </div>
    );
};

export default ImageUpload;
